import React, { useState, useEffect } from "react";
import { Shield } from "lucide-react";
import { apiClient } from "../../../utils/apiClient";
import { useAlert } from "../../../hooks/useAlert";
import { AuditLogEntry, AuditLogPagination } from "../../../types/audit";
import Pagination from "../../../components/common/ui/pagination/Pagination";
import ActivityLogItem from "../../../components/common/activity/ActivityLogItem";

interface FileAuditLogsProps {
  fileId?: string;
  resourceType: "file" | "form_submission";
  resourceId: string;
}

export const FileAuditLogs: React.FC<FileAuditLogsProps> = ({
  fileId,
  resourceType,
  resourceId
}) => {
  const { setAlert } = useAlert();
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [actionFilter, setActionFilter] = useState<string>("all");
  const [pagination, setPagination] = useState<AuditLogPagination>({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  // Support legacy fileId prop
  const actualResourceId = resourceId || fileId;
  const actualResourceType = resourceType || "file";

  useEffect(() => {
    loadAuditLogs();
  }, [actualResourceId, currentPage, actionFilter]);

  const loadAuditLogs = async () => {
    setLoading(true);
    try {
      const response = await apiClient.getAuditLogs({
        resourceType: actualResourceType,
        resourceId: actualResourceId!,
        action: actionFilter !== "all" ? actionFilter : undefined,
        page: currentPage,
        limit: 10,
      });

      setAuditLogs(response.logs);
      setPagination(response.pagination);
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message: `Failed to load ${actualResourceType === 'file' ? 'file' : 'form submission'} audit logs`,
      });
      setAuditLogs([]);
      setPagination({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
      });
    } finally {
      setLoading(false);
    }
  };


  const resourceLabel = actualResourceType === 'file' ? 'File' : 'Form Submission';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-black dark:text-white">
            {resourceLabel} Audit Log
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Track all {actualResourceType === 'file' ? 'downloads, shares, and access to this file' : 'views, shares, and access to this form submission'}
          </p>
        </div>

        {/* Action Filter */}
        <select
          value={actionFilter}
          onChange={(e) => {
            setActionFilter(e.target.value);
            setCurrentPage(1);
          }}
          className="h-9 rounded-lg border border-gray-300 bg-transparent px-3 py-1 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-2 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
        >
          <option value="all">All Actions</option>
          {actualResourceType === 'file' && (
            <option value="file_download">Downloads</option>
          )}
          {actualResourceType === 'form_submission' && (
            <>
              <option value="form_submitted">Submissions</option>
              <option value="form_submission_viewed">Views</option>
            </>
          )}
          <option value="share_link_accessed_success">Share Link Access</option>
          <option value="share_link_created">Share Link Created</option>
          <option value="share_link_revoked">Share Link Revoked</option>
        </select>
      </div>

      {/* Audit Logs List */}
      <div className="rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]">
        {loading ? (
          <div className="flex min-h-[200px] items-center justify-center">
            <p className="text-gray-500 dark:text-gray-400">Loading...</p>
          </div>
        ) : auditLogs.length === 0 ? (
          <div className="flex min-h-[200px] flex-col items-center justify-center p-8">
            <Shield size={48} className="mb-4 text-gray-300 dark:text-gray-700" />
            <p className="text-gray-500 dark:text-gray-400">No audit logs found</p>
            <p className="mt-2 text-sm text-gray-400 dark:text-gray-500">
              Audit logs will appear here as the {actualResourceType === 'file' ? 'file' : 'form submission'} is accessed
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 p-5 dark:divide-gray-800 md:p-6">
            {auditLogs.map((log) => (
              <ActivityLogItem
                key={log.id}
                entry={log}
                variant="audit"
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && auditLogs.length > 0 && pagination.totalPages > 1 && (
          <div className="border-t border-gray-200 p-5 pt-4 dark:border-gray-800 md:p-6 md:pt-4">
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              total={pagination.total}
              pageSize={pagination.limit}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default FileAuditLogs;
